import { memo, useState } from "react";
import { X } from "lucide-react";
import type { GoldenSerenityComponentProps } from "./types";
import { GS } from "./utils";
import { AmbientGlow, OrganicDivider } from "./decorations";

export const GoldenSerenityGallery = memo(({ event, config, t }: GoldenSerenityComponentProps) => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const gallery = event.gallery ?? [];

  if (gallery.length === 0 || !(event.showGallery ?? true)) return null;

  return (
    <section
      id="gallery"
      className="relative py-20 sm:py-28 overflow-hidden"
      style={{ background: `linear-gradient(to bottom, ${GS.cream}, ${GS.creamDeep})` }}
    >
      <AmbientGlow />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center mb-12 sm:mb-16">
          <div className="mb-8">
            <OrganicDivider width={80} />
          </div>
          <h2
            className="text-4xl sm:text-6xl font-medium mb-3"
            style={{ color: config.palette.text, fontFamily: config.typography.heading }}
          >
            {t("events.form.template.momentsWeTreasure")}
          </h2>
          <p
            className="text-sm tracking-wide"
            style={{ color: config.palette.mutedText, fontFamily: config.typography.body }}
          >
            {t("events.form.template.gallery")}
          </p>
        </div>

        {/* Photo grid with natural frames */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-5">
          {gallery.map((src, index) => (
            <button
              key={index}
              type="button"
              className="group cursor-pointer text-left"
              onClick={() => setSelectedImage(index)}
              aria-label={`${t("events.form.template.gallery")} ${index + 1}`}
            >
              <div
                className="relative p-1.5 sm:p-2 rounded-sm transition-all duration-500"
                style={{
                  background: `linear-gradient(to bottom right, ${GS.border}, ${GS.frame})`,
                  boxShadow: "0 4px 24px rgba(139,115,85,0.12)",
                }}
              >
                <div
                  className="p-2 sm:p-4 rounded-sm"
                  style={{ background: `linear-gradient(to bottom right, ${GS.creamLight}, ${GS.cream})` }}
                >
                  <div className="aspect-square overflow-hidden rounded-sm relative">
                    <img
                      src={src}
                      alt={`${t("events.form.template.gallery")} ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                      loading="lazy"
                      decoding="async"
                    />
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      style={{ background: "linear-gradient(to top, rgba(201,168,106,0.10), transparent)" }}
                    />
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage !== null && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 backdrop-blur-xl"
          style={{ background: "rgba(139,115,85,0.95)" }}
          onClick={() => setSelectedImage(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 sm:top-8 sm:right-8 p-3 sm:p-4 rounded-full transition-all duration-300"
            style={{ background: GS.creamLight, boxShadow: "0 4px 16px rgba(139,115,85,0.2)" }}
            aria-label={t("common.close")}
          >
            <X className="w-5 h-5" style={{ color: config.palette.text }} aria-hidden="true" />
          </button>

          <div className="max-w-5xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <div
              className="p-2 sm:p-3 rounded-sm"
              style={{
                background: `linear-gradient(to bottom right, ${GS.border}, ${GS.frame})`,
                boxShadow: "0 20px 80px rgba(139,115,85,0.3)",
              }}
            >
              <div
                className="p-3 sm:p-10 rounded-sm"
                style={{ background: `linear-gradient(to bottom right, ${GS.creamLight}, ${GS.cream})` }}
              >
                <img
                  src={gallery[selectedImage]}
                  alt={`${t("events.form.template.gallery")} ${selectedImage + 1}`}
                  className="w-full h-full object-contain max-h-[70vh] rounded-sm"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
});

GoldenSerenityGallery.displayName = "GoldenSerenityGallery";
